import { convertPriceTotalWithRounding } from '@apps/card/hooks/convertPriceTotalWithRounding';
import {
  StationeryDiscountInfo,
  StationeryPriceInfo,
  StationeryPriceInfoRawLineItem,
  StationeryProduct,
  StationeryProductAttribute,
  StationeryProductAttributeOption,
  StationeryProductVariantGroup,
} from './types';
import { calculateStationeryPricing } from './utils/calculatePrices';
import { calculateDiscount } from './utils/calculateDiscounts';
import { useShippingMethodLabelsAndDescriptions } from '@apps/card/hooks/useShippingMethodLabelsAndDescriptions';
import { addError } from '@shared/utils/logger';
import { CurrencyRateData } from '@apps/card/hooks/useCurrencyConversionRate';

class PrintPriceCalculator {
  constructor(
    private products: StationeryProduct[],
    private shippingMethodLabelsAndDescriptions: ReturnType<typeof useShippingMethodLabelsAndDescriptions>,
    private conversionRate?: CurrencyRateData,
  ) {}

  private getConversionRateMultiplier(): number {
    if (!this.conversionRate) return 1;

    return this.conversionRate.exchangeRate * this.conversionRate.taxesAndFeesMultiplier;
  }

  /**
   * Applies conversion rate and fixed cost per card to a unit price.
   * Converts a unit price using the formula:
   * ((priceInMinorUnits * getConversionRateMultiplier()) + fixedCostPerCardInMinorUnits)
   */
  private convertUnitPrice(unitPriceInMinorUnits: number): number {
    if (!this.conversionRate || unitPriceInMinorUnits === 0) return unitPriceInMinorUnits;

    const multiplier = this.getConversionRateMultiplier();
    const fixedCostPerCard = this.conversionRate.fixedCostPerCardInMinorUnits;
    return Math.round(unitPriceInMinorUnits * multiplier + fixedCostPerCard);
  }

  /**
   * Applies conversion rate and fixed cost per card to a total price.
   * Converts the total directly to preserve decimal precision, then adds fixed cost per card * quantity.
   */
  private convertTotalPrice(totalPriceInMinorUnits: number, quantity: number): number {
    if (!this.conversionRate || totalPriceInMinorUnits === 0) return totalPriceInMinorUnits;

    const multiplier = this.getConversionRateMultiplier();
    const fixedCostPerCard = this.conversionRate.fixedCostPerCardInMinorUnits;
    // Convert total directly to preserve decimal precision, then add fixed cost per card * quantity
    return Math.round(totalPriceInMinorUnits * multiplier) + fixedCostPerCard * quantity;
  }

  private correctRounding(totalPriceInMinorUnits: number, quantity: number, product: StationeryProduct): { resultWithFloor: number } {
    // Digital Cards do not need rounding correction because
    // they are displayed as 1 product on the Stripe checkout page.
    if (product.productType === 'digitalCard') {
      return {
        resultWithFloor: totalPriceInMinorUnits,
      };
    }

    return convertPriceTotalWithRounding(totalPriceInMinorUnits, quantity);
  }

  private getProductById(productId: string): StationeryProduct {
    const product = this.products.find(p => p.id === productId);

    if (!product) {
      throw new Error(`No product matching given productId (${productId}) found`);
    }

    return product;
  }

  private findAttributeById(product: StationeryProduct, attributeId: string): StationeryProductAttribute | null {
    const attribute = product.attributes.find(attr => attr.attributeId === attributeId) ?? null;
    return attribute;
  }

  private findProductAttributeOption(product: StationeryProduct, attributeId: string, optionId: string): StationeryProductAttributeOption {
    const targetAttribute = this.findAttributeById(product, attributeId);

    if (!targetAttribute) {
      throw new Error(`No attribute found with id ${attributeId}`);
    }

    let targetAttributeOption = targetAttribute.attributeOptions.find(opt => opt.attributeOption.id === optionId);

    // HACK: Provide the legacy smooth option for legacy orders, while we figure out how to have Price Config to support discontinued attributes.
    if (!targetAttributeOption && attributeId === 'paperType' && optionId === 'smooth') {
      targetAttributeOption = {
        __typename: 'StationeryProductAttributeOption',
        attributeOption: {
          __typename: 'StationeryAttributeOption',
          attributeId: 'paperType',
          id: 'smooth',
        },
        pricing: {
          __typename: 'StationeryFlatFeePricing',
          priceInMinorUnits: 0,
        },
      };
    }

    if (!targetAttributeOption) {
      throw new Error(`No option found with id ${optionId} on attribute ${attributeId}`);
    }

    return targetAttributeOption;
  }

  private findProductOptions(product: StationeryProduct, attributeOptions: { attributeId: string; optionId: string }[]): StationeryProductAttributeOption[] {
    // Search through all options, and select ones that are included in optionIds input.
    const options = attributeOptions.reduce((accumulator, option) => {
      const attributeOption = this.findProductAttributeOption(product, option.attributeId, option.optionId);
      return attributeOption ? [...accumulator, attributeOption] : accumulator;
    }, new Array<StationeryProductAttributeOption>());

    return options;
  }

  // Delivery price is included in preDiscountedPrice
  private applyDiscountToTotal(totalPriceInMinorUnits: number, discountInfo?: StationeryDiscountInfo, deliveryPrice?: number) {
    if (!discountInfo) {
      return { totalPriceInMinorUnits, hasDiscount: false as const };
    }

    const discountPriceValues = calculateDiscount(discountInfo, totalPriceInMinorUnits, deliveryPrice);
    return discountPriceValues;
  }

  private calculatePriceWithoutOptions = (product: StationeryProduct, quantity: number, discount?: StationeryDiscountInfo): StationeryPriceInfo => {
    const lineItems = new Array<StationeryPriceInfoRawLineItem>();
    const baseProductPrice = calculateStationeryPricing(product.pricing, quantity);

    const correctedBasePrice = this.correctRounding(baseProductPrice, quantity, product).resultWithFloor;

    const baseUnitPrice = correctedBasePrice / quantity;
    lineItems.push({
      totalPriceInMinorUnits: this.convertTotalPrice(correctedBasePrice, quantity),
      unitPriceInMinorUnits: this.convertUnitPrice(baseUnitPrice),
      quantity,
      type: 'base_cost',
    });

    // Original price --> Apply discounts if exist --> apply rounding correction to make even for Stripe
    const originalTotalPrice = baseProductPrice;
    const discountedPriceInfo = this.applyDiscountToTotal(originalTotalPrice, discount);
    const { totalPriceInMinorUnits: discountedTotal } = discountedPriceInfo;
    const totalPrice = this.correctRounding(discountedTotal, quantity, product).resultWithFloor;

    // floor might not be needed as the goal is convertPriceTotalWithRounding
    // is to make total perfectly divisible by quantity, but adding to be safe.
    const unitPrice = Math.floor(totalPrice / quantity);

    const canReturnDiscountInfo = discountedPriceInfo.hasDiscount && discount;

    return {
      totalPriceInMinorUnitsUSD: totalPrice,
      totalPriceInMinorUnits: this.convertTotalPrice(totalPrice, quantity),
      unitPriceInMinorUnits: this.convertUnitPrice(unitPrice),
      deliveryCost: 0,
      deliveryCostInUSD: 0,
      discounts: canReturnDiscountInfo
        ? {
            promoInfo: discount,
            savingsInMinorUnits: this.convertTotalPrice(discountedPriceInfo.savingsInMinorUnits, quantity),
            preDiscountPriceInMinorUnits: this.convertTotalPrice(discountedPriceInfo.preDiscountPriceInMinorUnits, quantity),
            preDiscountedUnitPriceInMinorUnits: this.convertUnitPrice(Math.floor(discountedPriceInfo.preDiscountPriceInMinorUnits / quantity)),
          }
        : undefined,
      lineItems,
    };
  };

  private calculatePriceWithOptions(
    product: StationeryProduct,
    optionIds: { attributeId: string; optionId: string }[],
    quantity: number,
    shippingMethodID?: string,
    discount?: StationeryDiscountInfo,
    salesTax?: number | null,
  ): StationeryPriceInfo {
    const selectedOptions = this.findProductOptions(product, optionIds);
    const lineItems = new Array<StationeryPriceInfoRawLineItem>();
    const baseProductPrice = calculateStationeryPricing(product.pricing, quantity);

    const correctedBasePrice = this.correctRounding(baseProductPrice, quantity, product).resultWithFloor;

    // Sum up extended price of all options
    const optionsPriceInUSD = selectedOptions.reduce((totalPrice, option) => {
      const optionPriceConfig = option.pricing;
      const optionTotalPrice = calculateStationeryPricing(optionPriceConfig, quantity);
      // this is for line item display only
      const convertedOptionTotalPrice = Math.round(optionTotalPrice * this.getConversionRateMultiplier());

      lineItems.push({
        totalPriceInMinorUnits: convertedOptionTotalPrice,
        attributeId: option.attributeOption.attributeId,
        optionId: option.attributeOption.id,
        type: 'option',
      });

      return totalPrice + optionTotalPrice;
    }, 0);

    // Get price for delivery option
    const deliveryOption = product.shippingOptions?.find(o => o.id === shippingMethodID);
    if (shippingMethodID && !deliveryOption) {
      throw new Error(`Unknown delivery method id ${shippingMethodID} provided for product ${product.id}`);
    }

    const deliveryPrice = deliveryOption ? calculateStationeryPricing(deliveryOption.pricing, quantity) : 0;

    if (deliveryOption) {
      const deliveryLabel = this.shippingMethodLabelsAndDescriptions[deliveryOption.id as keyof typeof this.shippingMethodLabelsAndDescriptions].title;

      if (!deliveryLabel) {
        addError('Invalid delivery label for line item.', { deliveryId: deliveryOption.id, productId: product.id });
      }

      lineItems.push({
        type: 'delivery',
        totalPriceInMinorUnits: Math.round(deliveryPrice * this.getConversionRateMultiplier()),
        deliveryMethodId: deliveryOption.id,
        deliveryMethodLabel: deliveryLabel,
      });
    }

    if (salesTax) {
      lineItems.push({
        type: 'tax',
        totalPriceInMinorUnits: salesTax,
      });
    }

    // Original price --> Apply discounts if exist --> apply rounding correction to make even for Stripe
    const originalTotalPrice = baseProductPrice + optionsPriceInUSD;
    const discountedPriceInfo = this.applyDiscountToTotal(originalTotalPrice, discount, deliveryPrice);
    const { totalPriceInMinorUnits: discountedTotal } = discountedPriceInfo;
    const totalPrice = this.correctRounding(discountedTotal, quantity, product).resultWithFloor;

    // floor might not be needed as the goal is convertPriceTotalWithRounding
    // is to make total perfectly divisible by quantity, but adding to be safe.
    const unitPrice = Math.floor(totalPrice / quantity);

    const canReturnDiscountInfo = discountedPriceInfo.hasDiscount && discount;

    lineItems.unshift({
      totalPriceInMinorUnits: this.convertTotalPrice(correctedBasePrice, quantity),
      unitPriceInMinorUnits: this.convertUnitPrice(unitPrice),
      quantity,
      type: 'base_cost',
    });

    return {
      totalPriceInMinorUnitsUSD: totalPrice + deliveryPrice + (salesTax ?? 0),
      totalPriceInMinorUnits: Math.round(this.convertTotalPrice(totalPrice, quantity) + deliveryPrice * this.getConversionRateMultiplier() + (salesTax ?? 0)),
      unitPriceInMinorUnits: this.convertUnitPrice(unitPrice),
      deliveryCost: Math.round(deliveryPrice * this.getConversionRateMultiplier()),
      deliveryCostInUSD: deliveryPrice,
      discounts: canReturnDiscountInfo
        ? {
            promoInfo: discount,
            savingsInMinorUnits: this.convertTotalPrice(discountedPriceInfo.savingsInMinorUnits, quantity),
            preDiscountPriceInMinorUnits: this.convertTotalPrice(discountedPriceInfo.preDiscountPriceInMinorUnits, quantity),
            preDiscountedUnitPriceInMinorUnits: this.convertUnitPrice(Math.floor(discountedPriceInfo.preDiscountPriceInMinorUnits / quantity)),
          }
        : undefined,
      lineItems,
    };
  }

  private variantGroupContains(group: StationeryProductVariantGroup, attributes: { attributeId: string; id: string }[]): boolean {
    const groupOptions = group.options;

    // Are ANY option from the variant group present in the given list of attributes?
    return groupOptions.some(option => {
      const matchingOptionOnAttributes = attributes.find(attr => {
        return attr.attributeId === option.attributeId && attr.id === option.id;
      });

      return matchingOptionOnAttributes !== undefined;
    });
  }

  /**
   * Does this variant group match the given set of attributes?
   */
  private variantGroupMatches(group: StationeryProductVariantGroup, attributes: { attributeId: string; id: string }[]): boolean {
    const groupOptions = group.options;

    // Is every option from the variant group present in the given list of attributes?
    return groupOptions.every(option => {
      const matchingOptionOnAttributes = attributes.find(attr => {
        return attr.attributeId === option.attributeId && attr.id === option.id;
      });

      return matchingOptionOnAttributes !== undefined;
    });
  }

  /**
   * Does this product have a variant group which explicitly matches ALL given attributes?
   */
  private validateVariantGroup(productId: string, attributes: { attributeId: string; id: string }[]): boolean {
    const product = this.getProductById(productId);
    const variantGroups = product.variantGroups;
    return variantGroups.some(group => this.variantGroupMatches(group, attributes));
  }

  private getVariantGroupForOptions(productId: string, attributes: { attributeId: string; id: string }[]): StationeryProductVariantGroup | undefined {
    const product = this.getProductById(productId);
    const variantGroups = product.variantGroups;
    return variantGroups.find(group => this.variantGroupMatches(group, attributes));
  }

  public getBestVariantGroupForChange(productId: string, selection: { attributeId: string; optionId: string }): StationeryProductVariantGroup | undefined {
    const product = this.getProductById(productId);
    const variantGroups = product.variantGroups;

    const mappedSelection = {
      id: selection.optionId,
      attributeId: selection.attributeId,
    };

    return variantGroups.find(group => this.variantGroupContains(group, [mappedSelection]));
  }

  /**
   * Will making this selection result in a valid variant group? Or will we need to snap to closest variant group?
   */
  public doesSelectionCreateValidVariantGroup(
    productId: string,
    state: { attributeId: string; optionId: string }[],
    selection: { attributeId: string; optionId: string },
  ): boolean {
    const product = this.getProductById(productId);
    const stateAfterSelection = [...state.filter(attr => attr.attributeId !== selection.attributeId), selection];
    const stateWithMappedNames = stateAfterSelection.map(attr => {
      return {
        attributeId: attr.attributeId,
        id: attr.optionId,
      };
    });

    if (product.variantGroups.length === 0) {
      return true;
    }

    return this.validateVariantGroup(productId, stateWithMappedNames);
  }

  public getVariantGroupForChoices(productId: string, options: { attributeId: string; optionId: string }[]): string | null {
    const mapped = options.map(o => {
      return {
        id: o.optionId,
        attributeId: o.attributeId,
      };
    });

    return this.getVariantGroupForOptions(productId, mapped)?.id ?? null;
  }

  public getOptionPrice(productId: string, optionInput: { attributeId: string; optionId: string }, quantity: number): number {
    const product = this.getProductById(productId);
    const option = this.findProductOptions(product, [optionInput])[0];

    if (!option) {
      throw new Error(`No option found with id ${optionInput.optionId} on product ${productId}`);
    }

    const totalPrice = calculateStationeryPricing(option.pricing, quantity);
    return totalPrice ? totalPrice * this.getConversionRateMultiplier() : 0;
  }

  /**
   * Gets the basic price for a product with default attributes.
   * @param productId Product to get price for
   * @param quantity Optional quantity. Defaults to 100.
   * @param excludeOptions Excludes the price of options. Use if you're also displaying itemized prices on this screen. Used on PDP product selector to show BASE base price of product.
   */
  public getProductBasePrice(productId: string, quantity = 100, discount?: StationeryDiscountInfo, excludeOptions?: boolean): StationeryPriceInfo {
    const product = this.getProductById(productId);
    const attributes = product.attributes;

    // Get option ID of first option for all attributes.
    const firstChoiceOptions = attributes.map(attribute => {
      return {
        attributeId: attribute.attributeId,
        optionId: attribute.attributeOptions[0].attributeOption.id,
      };
    });

    return excludeOptions
      ? this.calculatePriceWithoutOptions(product, quantity, discount)
      : this.calculatePriceWithOptions(product, firstChoiceOptions, quantity, undefined, discount);
  }

  /**
   * @param extraPrices Use to hack in non-attribute prices. This should really only be used for shipping.
   */
  public getProductPrice(
    productId: string,
    quantity: number,
    optionChoices: { attributeId: string; optionId: string }[],
    shippingMethodID?: string,
    discount?: StationeryDiscountInfo,
    salesTax?: number | null,
  ): StationeryPriceInfo {
    const product = this.getProductById(productId);
    const priceInfo = this.calculatePriceWithOptions(product, optionChoices, quantity, shippingMethodID, discount, salesTax);

    // Add in extra prices from non-attribute sources.
    // Should really only be used for shipping.
    // If it's affecting the price, it should be an attribute that
    // is accounted for in the product so we can control it via
    // price config and keep our logic consolidated.

    return priceInfo;
  }
}

export default PrintPriceCalculator;
